import * as React from "react";
import {useEffect, useState} from "react";
import {Map} from "immutable";
import {ProductSummary} from "../../model/product/ProductSummary";
import {getSuccessData} from "../common/commonStates";
import {
    currentPlan,
    hasAllAccessEnabled,
    hasClientPortfolioAccess,
    hasClientResearchEnabled,
    hasDynamicResearchAccess,
    hasPortfolioAccess,
    hasResearchAccess,
    isEditingPlanPortfolio,
    planCountryName,
    portfolioProductSummariesSelector
} from "../../../mainReducerMapSelectors";
import {IApplicationRootState} from "../../../applicationState";
import {connect, useDispatch, useSelector} from "react-redux";
import {IMrtTableProps, MrtTableComponent} from "../common/MrtTable.component";
import {MRT_Cell, MRT_ColumnDef} from "material-react-table";
import {FormattedMessage, injectIntl, IntlShape} from "react-intl";
import {
    DEFAULT_PAGINATION,
    IMrtTableColumnFilter,
    IMrtTablePagination,
    IMrtTableState
} from "../../model/MrtTableState.model";
import {Link} from "react-router-dom";
import researchPageActions from "./ResearchPageActions";
import {scrollToElement} from "../../utils/browserUtil";
import {getTranslation} from "../../utils/translationUtil";
import {hasConsultantLevelAccess, hasResearchPageAccess} from "../../utils/sessionUtil";
import {RaisedButton} from "../common/RaisedButton";
import {defaultButtonStyle, prominentButtonStyle} from "../common/buttonStyles";
import {AdminActionTypes} from "./edit-portfolio/editPortfolioActions";
import {IPlanInfo} from "../../model/ClientsAndPlans.model";
import productActions from "../product/productActions";
import {NoAccessNotification} from "../base/header/NoAccessNotification";
import {ErrorComponent} from "../base/Error.component";
import {bindActionCreators, Dispatch} from "redux";
import {byRatingAndManagerNameAndProductName, customSearchFn, fixBlankAndTranslate} from "../../utils/researchUtil";
import {SliderComponent} from "../common/Slider.component";
import {NOT_RATED, NR_RATING} from "../../utils/ratingConstants";
import {RatingDefinitions} from "./RatingDefinitions";
import Tooltip from "@mui/material/Tooltip";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {AnyThunkDispatch} from "../../../commonTypes";

export interface IResearchPropsFromStore {
    portfolioProductSummaries: Map<number, ProductSummary> | null;
    hasDynamicResearchAccess: boolean;
    hasAllAccessEnabled: boolean;
    planCountryName: string;
    currentPlan: IPlanInfo | undefined;
    hasClientResearchEnabled: boolean;
    hasResearchAccess: boolean;
    hasPortfolioAccess: boolean;
    clientPortfolioEnabled: boolean;
    editingPlanPortfolio: boolean;
}

export interface IResearchPropsFromActions {
    actions: {
        removeProductFromPlanPortfolio: (backstopId: number) => void;
    };
}

export interface IResearchIntlProps {
    intl: IntlShape;
}

export type IResearchProps = IResearchPropsFromStore & IResearchPropsFromActions & IResearchIntlProps;

export const CLIENT_USER_HIDDEN_MESSAGE = "The Managers & Strategies section is currently hidden from client users of this plan.";
export const ADD_STRATEGIES_MESSAGE = "To add strategies: find the strategy detail page using the search box above " +
    "and select “Add to Portfolio”.";
export const REMOVE_STRATEGIES_MESSAGE = "To remove strategies: click the product name to expand and " +
    "select “Remove from Portfolio”.";

export const ResearchPage: React.FunctionComponent<IResearchProps> = (props) => {
    const [data, setData] = useState<ProductSummary[]>([]);
    const [resetTable, setResetTable] = useState<boolean>(false);
    const [displaySlider, setDisplaySlider] = useState<boolean>(false);

    const dispatch: AnyThunkDispatch = useDispatch();

    const researchTableStateFromStore: IMrtTableState | undefined
        = useSelector((state: IApplicationRootState) => state.researchPageState);

    useEffect(() => {
        if (props.portfolioProductSummaries) {
            const productSummaries = getFixedProductSummaries().sort(byRatingAndManagerNameAndProductName);
            setData(productSummaries);
        }
    }, [props]);

    useEffect(() => {
        if (researchTableStateFromStore?.scrollToBackstopId) {
            setTimeout(() => {
                scrollToElement(`#id_${researchTableStateFromStore.scrollToBackstopId}`, 100, true);
            }, (50));
        }
    }, []);

    const managerNameLink = ({cell}: { cell: MRT_Cell<ProductSummary> }) => {
        const mangerName = cell.getValue<string>();
        const backstopId = cell.row.original.manager.managerBackstopId;
        return (
            <Link to={`/managers/${backstopId}`}
                  onClick={() => dispatch(researchPageActions.setScrollToBackstopAction(backstopId))}
            >
                <div id={`id_${backstopId}`}>{mangerName}</div>
            </Link>
        );
    };
    const strategyNameLink = ({cell}: { cell: MRT_Cell<ProductSummary> }) => {
        const productName = cell.getValue<string>();
        const backstopId = cell.row.original.backstopId;
        return (
            <Link to={`/products/${backstopId}`}
                  onClick={() => dispatch(researchPageActions.setScrollToBackstopAction(backstopId))}>
                <div id={`id_${backstopId}`}>{productName}</div>
            </Link>
        );
    };

    const removeProductLink = ({cell}: { cell: MRT_Cell<ProductSummary> }) => {
        const backstopId = cell.row.original.backstopId;
        return <div className="new-research__remove-link"
                    onClick={() => props.actions.removeProductFromPlanPortfolio(backstopId)}>X</div>;
    };

    const mapAndTranslateOddRating = (oddRating: string) => {
        oddRating = oddRating.replace(NR_RATING, NOT_RATED);
        return getTranslation(`research-product-rating.${oddRating}`, oddRating);
    };

    const dataMapper = (productSummary: ProductSummary) => {
        return {
            ...productSummary,
            managerName: productSummary.manager.name,
            oddRatingCurrent: mapAndTranslateOddRating(productSummary.oddRating.rating),
        };
    };

    const getFixedProductSummaries = (): ProductSummary[] => {
        if (!props.portfolioProductSummaries) {
            return [];
        }

        return props.portfolioProductSummaries
            .map(dataMapper)
            .valueSeq()
            .toArray()
            .map((it: ProductSummary) => fixBlankAndTranslate(it));
    };

    const renderInfoIcon = (header: string) => {
        return (
            <div>
                <Tooltip title={getTranslation("research-page.rating.definitions", "Rating Definitions")}>
                    <i className="new-research__info-icon fal fa-info-circle"
                       onClick={() => setDisplaySlider(true)}
                       data-testid="new-research__info-icon"/>
                </Tooltip>
                {header}
            </div>
        );
    };

    const columns: MRT_ColumnDef<ProductSummary>[] = [
        {
            accessorKey: "managerName",
            header: getTranslation("research-page.manager-name", "Manager Name"),
            Cell: managerNameLink,
            size: 180,
            filterVariant: "text"
        },
        {
            accessorKey: "name",
            header: getTranslation("research-page.strategy-name", "Strategy Name"),
            Cell: strategyNameLink,
            size: 200,
            filterVariant: "text"
        },
        {
            accessorKey: "assetType",
            header: getTranslation("research-page.asset-class", "Asset Class"),
            enableEditing: true,
            size: 90
        },
        {
            accessorKey: "productRating",
            header: getTranslation("research-page.rating", "Rating"),
            Header: () => renderInfoIcon(getTranslation("research-page.rating", "Rating")),
            size: 90
        },
        {
            accessorKey: "oddRatingCurrent",
            header: getTranslation("research-page.odd-rating", "ODD Rating"),
            Header: () => renderInfoIcon(getTranslation("research-page.odd-rating", "ODD Rating")),
            size: 90
        },
        {accessorKey: "approach", header: getTranslation("research-page.approach", "Approach"), size: 90},
        {accessorKey: "category", header: getTranslation("research-page.category", "Category"), size: 90},
        {accessorKey: "region", header: getTranslation("research-page.region", "Region"), size: 90},
        {accessorKey: "style", header: getTranslation("research-page.style", "Style"), size: 90},
        {
            accessorKey: "remove",
            header: getTranslation("research-page.remove", "Remove"),
            Cell: removeProductLink,
            enableColumnFilter: false,
            size: 90
        },
    ];

    const onPaginationChange = (pagination: IMrtTablePagination) => {
        dispatch(researchPageActions.updatePaginationAction(pagination));
        setResetTable(false);
    };

    const onGlobalFilterChange = (value: string) => {
        dispatch(researchPageActions.updateGlobalFilterAction(value));
        setResetTable(false);
    };

    const onColumnFiltersChange = (filters: IMrtTableColumnFilter[]) => {
        dispatch(researchPageActions.updateColumnFiltersAction(filters));
        setResetTable(false);
    };

    const renderEditControlButton = () => {
        if (!hasConsultantLevelAccess() || props.hasDynamicResearchAccess
            || props.hasAllAccessEnabled || props.planCountryName === "UK") {
            return;
        }

        return <div>
            {props.editingPlanPortfolio ? renderDoneEditingButton() : renderEditPortfolioButton()}
        </div>;
    };

    const tableOptions: IMrtTableProps<ProductSummary> = {
        columns,
        data,
        header: renderEditControlButton,
        onColumnFiltersChange,
        onGlobalFilterChange,
        onPaginationChange,
        columnFilters: researchTableStateFromStore?.columnFilters ? researchTableStateFromStore.columnFilters : [],
        globalFilter: researchTableStateFromStore?.globalFilterState
            ? researchTableStateFromStore!.globalFilterState
            : "",
        pagination: researchTableStateFromStore?.pagination
            ? researchTableStateFromStore.pagination
            : DEFAULT_PAGINATION,
        reset: resetTable,
        columnVisibility: props.editingPlanPortfolio ? {} : {remove: false},
        customSearchFn
    };

    const renderEditingPortfolioHeader = () => {
        return <div className="new-research__editing-header" data-testid="editing-header">
            <h1>Editing Client Portfolio: {props.currentPlan?.name}</h1>
            <div className="new-research__help-text">
                <div>{REMOVE_STRATEGIES_MESSAGE}</div>
                <div>{ADD_STRATEGIES_MESSAGE}</div>
            </div>
        </div>;
    };

    const renderEditPortfolioButton = () => {
        const onChangeHandler = () => {
            dispatch({type: AdminActionTypes.BEGIN_EDIT_PORTFOLIO});
        };

        return <RaisedButton
            className="new-research__edit-portfolio-button"
            style={ defaultButtonStyle }
            onClick={onChangeHandler}
            primary={true}
        >
            <FormattedMessage id="research.edit-portfolio" defaultMessage="Edit Portfolio" />
        </RaisedButton>;
    };

    const renderDoneEditingButton = () => {
        const onChangeHandler = () => {
            dispatch({type: AdminActionTypes.END_EDIT_PORTFOLIO});
        };

        return <RaisedButton
            className="new-research__done-portfolio-button"
            style={prominentButtonStyle}
            onClick={onChangeHandler}
            primary={true}>
            <FormattedMessage id="research.done-editing" defaultMessage="Done Editing" />
        </RaisedButton>;
    };

    const renderNoResearchNotification = () => {
        return !props.hasClientResearchEnabled
            ? <NoAccessNotification
                message={getTranslation("research.hidden-from-clients", CLIENT_USER_HIDDEN_MESSAGE)}/>
            : null;
    };

    const render = () => {
        if (!hasResearchPageAccess(
            props.hasResearchAccess, props.hasPortfolioAccess, props.clientPortfolioEnabled)) {
            return <ErrorComponent/>;
        }

        if (!props.portfolioProductSummaries) {
            return <LoadingSpinner/>;
        }

        return <div className="new-research__container" data-testid="new-research__container">
                <SliderComponent
                    hideSlider={() => setDisplaySlider(false)}
                    header={getTranslation("research-page.rating.definitions", "Rating Definitions")}
                    content={<RatingDefinitions />}
                    display={displaySlider}/>
                {renderNoResearchNotification()}
                {props.editingPlanPortfolio && renderEditingPortfolioHeader()}
                <MrtTableComponent {...tableOptions}/>
            </div>;
    };

    return render();
};

export const mapStateToProps = (state: IApplicationRootState): IResearchPropsFromStore => {
    return {
        portfolioProductSummaries: getSuccessData(portfolioProductSummariesSelector(state)),
        hasDynamicResearchAccess: hasDynamicResearchAccess(state),
        hasAllAccessEnabled: hasAllAccessEnabled(state),
        planCountryName: planCountryName(state),
        currentPlan: currentPlan(state),
        editingPlanPortfolio: isEditingPlanPortfolio(state),
        hasClientResearchEnabled: hasClientResearchEnabled(state),
        hasResearchAccess: hasResearchAccess(state),
        hasPortfolioAccess: hasPortfolioAccess(state),
        clientPortfolioEnabled: hasClientPortfolioAccess(state)
    };
};

export const mapDispatchToProps = (dispatch: Dispatch): IResearchPropsFromActions => {
    return {
        actions: bindActionCreators({
            removeProductFromPlanPortfolio: productActions.removeProductFromPlanPortfolio,
        }, dispatch),
    };
};

const connectedNewResearchPage = connect<IResearchPropsFromStore, IResearchPropsFromActions>(
    mapStateToProps,
    mapDispatchToProps
)(ResearchPage);

export default injectIntl(connectedNewResearchPage);
